import { StrictMode, lazy, Suspense, type ComponentType } from "react";
import { createRoot } from "react-dom/client";
import { RouterProvider, createBrowserRouter, Navigate } from "react-router-dom";
import { tokensToCss } from "@powwow/design-tokens/css";
import { Skeleton } from "@powwow/ui";
import { AppShell } from "./app-shell.js";
import { SignInScreen } from "./screens/sign-in.js";
// Eager, like sign-in: someone forced here cannot use the app until it renders, and a lazy chunk
// that fails to load would be a locked door.
import { SetPasswordScreen } from "./screens/set-password.js";
// Eager for the same reason as set-password: an invited person forced here has no session and no
// other way into the app, so a lazy chunk that failed to load would be a locked front door.
import { AcceptInvitationScreen } from "./screens/accept-invitation.js";
// Eager, both of them: the screen that explains a failure cannot itself be a chunk that might fail
// to load, and a 404 is most likely on a cold visit where the lazy chunk is not cached.
import { NotFoundScreen, RouteErrorScreen } from "./screens/not-found.js";
import "./styles.css";

// The brand goes into the document before React mounts, from the same generator the Astro site
// uses. One definition of the tokens, two consumers (ux.md §4).
const style = document.createElement("style");
style.textContent = tokensToCss(null, "desk");
document.head.prepend(style);

/**
 * Every screen is a chunk of its own.
 *
 * The app was a single 670 kB bundle, which is fine on an office laptop in June and is the thing
 * most likely to make it unusable at the gate in August. An arena device opens check-in and nothing
 * else; there is no reason for it to download the form builder, the audit viewer and the analytics
 * page to do that.
 *
 * The sign-in screen stays eager, because it is the first thing every visitor needs and a lazy
 * chunk there would only add a round trip before anyone can do anything.
 *
 * `lazy` wants a default export and these are named, so each import is unwrapped here rather than
 * a `export default` being added to every screen — the named export is what the rest of the app
 * imports, and having both would be two names for one component.
 */
function screen<K extends string>(
  load: () => Promise<Record<K, ComponentType>>,
  name: K,
): ComponentType {
  return lazy(async () => ({ default: (await load())[name] as ComponentType }));
}

const JudgeScreen = screen(() => import("./screens/judge.js"), "JudgeScreen");
const AdminScreen = screen(() => import("./screens/admin.js"), "AdminScreen");
const DashboardScreen = screen(() => import("./screens/dashboard.js"), "DashboardScreen");
const EventsScreen = screen(() => import("./screens/events.js"), "EventsScreen");
const EventScreen = screen(() => import("./screens/event.js"), "EventScreen");
const CategoriesScreen = screen(() => import("./screens/categories.js"), "CategoriesScreen");
const DeskScreen = screen(() => import("./screens/desk.js"), "DeskScreen");
const CheckInScreen = screen(() => import("./screens/check-in.js"), "CheckInScreen");
const JudgesScreen = screen(() => import("./screens/judges.js"), "JudgesScreen");
const ResultsScreen = screen(() => import("./screens/results.js"), "ResultsScreen");
const PayoutsScreen = screen(() => import("./screens/payouts.js"), "PayoutsScreen");
const PrizesScreen = screen(() => import("./screens/prizes.js"), "PrizesScreen");
const RolesScreen = screen(() => import("./screens/roles.js"), "RolesScreen");
const TeamsScreen = screen(() => import("./screens/teams.js"), "TeamsScreen");
const UpdatesScreen = screen(() => import("./screens/updates.js"), "UpdatesScreen");
const SettingsScreen = screen(() => import("./screens/settings.js"), "SettingsScreen");
const MembersScreen = screen(() => import("./screens/members.js"), "MembersScreen");
const FormsScreen = screen(() => import("./screens/forms.js"), "FormsScreen");
const FormBuilderScreen = screen(() => import("./screens/form-builder.js"), "FormBuilderScreen");
const ParticipantsScreen = screen(() => import("./screens/participants.js"), "ParticipantsScreen");
const ProfileScreen = screen(() => import("./screens/profile.js"), "ProfileScreen");
const NotificationsScreen = screen(
  () => import("./screens/notifications.js"),
  "NotificationsScreen",
);
const AnalyticsScreen = screen(() => import("./screens/analytics.js"), "AnalyticsScreen");
const ExportsScreen = screen(() => import("./screens/exports.js"), "ExportsScreen");
const SurveysScreen = screen(() => import("./screens/surveys.js"), "SurveysScreen");
const AuditScreen = screen(() => import("./screens/audit.js"), "AuditScreen");
const QrCodesScreen = screen(() => import("./screens/qr-codes.js"), "QrCodesScreen");
const HelpScreen = screen(() => import("./screens/help.js"), "HelpScreen");

/**
 * Real routes rather than conditional rendering.
 *
 * The address bar is part of the interface here: a check-in worker keeps one screen open all
 * afternoon, and someone chasing a readiness warning follows a link straight to the thing that
 * needs fixing. Both need a URL that survives a reload.
 */
const router = createBrowserRouter([
  { path: "/sign-in", element: <SignInScreen /> },
  // Both spellings reach the same screen: one is where a reset email points, the other is where a
  // temporary password sends you.
  { path: "/set-password", element: <SetPasswordScreen /> },
  { path: "/reset-password", element: <SetPasswordScreen /> },
  // Where a staff invitation email points. Outside the shell, like sign-in: the person arriving
  // here has no session yet, and acceptance is what creates it.
  { path: "/accept-invitation", element: <AcceptInvitationScreen /> },
  // Outside the staff shell on purpose: a judge signs in with a code and gets this, and only this.
  {
    path: "/judge",
    element: (
      <Suspense fallback={<Skeleton rows={5} />}>
        <JudgeScreen />
      </Suspense>
    ),
  },
  // Also outside the shell: the platform console is not about a community, so it has its own frame
  // and gates itself on `platform_admins` rather than on a community membership.
  {
    path: "/admin/communities",
    element: (
      <Suspense fallback={<Skeleton rows={5} />}>
        <AdminScreen />
      </Suspense>
    ),
  },
  {
    path: "/",
    element: <AppShell />,
    // Inside the shell, so a person who lands here still has the navigation they know.
    errorElement: <RouteErrorScreen />,
    children: [
      // The dashboard is where a signed-in person lands: it composes the panels their permissions
      // justify rather than making them choose which of their roles they are today.
      { index: true, element: <Navigate to="/dashboard" replace /> },
      { path: "dashboard", element: <DashboardScreen /> },
      { path: "events", element: <EventsScreen /> },
      { path: "events/:eventId", element: <EventScreen /> },
      { path: "events/:eventId/categories", element: <CategoriesScreen /> },
      { path: "events/:eventId/desk", element: <DeskScreen /> },
      { path: "events/:eventId/check-in", element: <CheckInScreen /> },
      { path: "events/:eventId/judges", element: <JudgesScreen /> },
      { path: "events/:eventId/teams", element: <TeamsScreen /> },
      { path: "events/:eventId/results", element: <ResultsScreen /> },
      { path: "events/:eventId/prizes", element: <PrizesScreen /> },
      { path: "events/:eventId/roles", element: <RolesScreen /> },
      { path: "events/:eventId/payouts", element: <PayoutsScreen /> },
      { path: "events/:eventId/updates", element: <UpdatesScreen /> },
      { path: "events/:eventId/qr-codes", element: <QrCodesScreen /> },
      // Community-wide, rather than about the event someone happens to be inside.
      { path: "participants", element: <ParticipantsScreen /> },
      { path: "forms", element: <FormsScreen /> },
      { path: "forms/:formId", element: <FormBuilderScreen /> },
      { path: "surveys", element: <SurveysScreen /> },
      { path: "analytics", element: <AnalyticsScreen /> },
      { path: "exports", element: <ExportsScreen /> },
      { path: "notifications", element: <NotificationsScreen /> },
      { path: "audit", element: <AuditScreen /> },
      { path: "members", element: <MembersScreen /> },
      { path: "settings", element: <SettingsScreen /> },
      { path: "profile", element: <ProfileScreen /> },
      // Help is for everyone with a session, so it carries no permission gate. The index lists
      // every topic; `:topic` renders one, and the contextual links across the app land here.
      { path: "help", element: <HelpScreen /> },
      { path: "help/:topic", element: <HelpScreen /> },
      // The readiness rules link to screens that arrive in later phases. Sending someone to the
      // event overview is a better answer than a blank page.
      { path: "events/:eventId/*", element: <EventScreen /> },
      // Anything else. A renamed route, a mistyped address, a link from an old email — all of them
      // are ordinary, and none of them should produce a stack trace.
      { path: "*", element: <NotFoundScreen /> },
    ],
  },
]);

const root = document.getElementById("root");
if (!root) throw new Error("No #root element");

createRoot(root).render(
  <StrictMode>
    <RouterProvider router={router} />
  </StrictMode>,
);
